// packages/web/src/components/teach/ExerciseCard.tsx
import React from 'react';
import { MathRenderer } from '../common/MathRenderer.v3';
import { Exercise } from '../../stores/teacherLearningStore';

interface ExerciseCardProps {
  exercise: Exercise;
  index: number;
  onSelect: (id: string) => void;
  onUpdateRequirement: (id: string, requirement: string) => void;
  onViewDetail: (exercise: Exercise) => void;
  onDelete: (id: string) => void;
  showSelection?: boolean;
}

export const ExerciseCard: React.FC<ExerciseCardProps> = ({
  exercise,
  index,
  onSelect,
  onUpdateRequirement,
  onViewDetail,
  onDelete,
  showSelection = false,
}) => {
  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  const getDifficultyStars = (difficulty: number) => {
    return '★'.repeat(difficulty) + '☆'.repeat(3 - difficulty);
  };

  const getDifficultyColor = (difficulty: number) => {
    const colors: Record<number, string> = {
      1: 'text-green-600',
      2: 'text-yellow-600',
      3: 'text-red-600',
    };
    return colors[difficulty] || 'text-gray-600';
  };

  return (
    <div className="exercise-card bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow mb-4">
      {/* 横向布局：4个栏目 */}
      <div className="grid grid-cols-12 gap-4 items-start">
        {/* 栏目1：标题和描述 (3列) */}
        <div className="col-span-3">
          <div className="title text-sm font-medium text-gray-800 mb-2">
            {exercise.title}
          </div>

          <div className="description text-xs text-gray-600 h-24 overflow-auto border border-gray-200 rounded p-2">
            <MathRenderer
              content={exercise.initial_question}
              className="exercise-description"
            />
          </div>
        </div>

        {/* 栏目2：教学要求 (3列) */}
        <div className="col-span-3">
          <label className="block text-xs text-gray-500 mb-1 font-medium">教学要求</label>
          <textarea
            value={exercise.teacher_requirement || ''}
            onChange={(e) => onUpdateRequirement(exercise.id, e.target.value)}
            placeholder="输入教学要求..."
            className="w-full h-28 text-xs border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        {/* 栏目3：知识点评估 (4列) */}
        <div className="col-span-4">
          <label className="block text-xs text-gray-500 mb-1 font-medium">知识点评估</label>
          {exercise.knowledge_points && exercise.knowledge_points.length > 0 ? (
            <div className="space-y-1 h-28 overflow-auto border border-gray-200 rounded p-2">
              {exercise.knowledge_points.map((kp, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1"
                >
                  <span className="text-gray-800 flex-1 truncate" title={kp.name}>
                    {kp.name}
                  </span>
                  <span
                    className={`text-sm font-bold ml-2 ${getDifficultyColor(kp.difficulty)}`}
                    title={`难度：${kp.difficulty}/3`}
                  >
                    {getDifficultyStars(kp.difficulty)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-400 border border-dashed border-gray-300 rounded p-2 h-28 flex items-center justify-center">
              暂无知识点信息
            </div>
          )}
        </div>

        {/* 栏目4：按钮和checkbox (2列) */}
        <div className="col-span-2 flex flex-col gap-2">
          {showSelection && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exercise.selected || false}
                onChange={() => onSelect(exercise.id)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-600">选择</span>
            </div>
          )}
          
          <button
            onClick={() => onViewDetail(exercise)}
            className="w-full text-xs px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors font-medium"
          >
            查看详情
          </button>
          
          <button
            onClick={() => onDelete(exercise.id)}
            className="w-full text-xs px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors font-medium"
          >
            删除
          </button>
        </div>
      </div>
    </div>
  );
};
